// Shared heat-ramp for the matrix panels (Market Opportunity, Reimbursement).
//
// Both panels draw the same thing — a market × sub-segment grid whose cells are
// shaded cream → terracotta by intensity — and both had grown their own verbatim
// copy of `ramp` / `textOn`, plus their own copy of the n/a hatch shades. One
// copy drifting from the other is exactly the "private dialect" problem the
// platform UI pass is trying to remove, so the ramp lives here now.
//
// The endpoints are LITERALS rather than var(--bg) / var(--accent) because the
// ramp interpolates numerically between them — a CSS variable can't be read
// channel-by-channel at this layer. They mirror tokens.css and must be updated
// together:
//
//   HEAT_FROM  #fff1e5  = --bg
//   HEAT_TO    #a8572a  = --accent
//
const HEAT_FROM: readonly [number, number, number] = [255, 241, 229];
const HEAT_TO: readonly [number, number, number] = [168, 87, 42];

const clamp = (x: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, x));

/** Cell fill for intensity `t` (0..1): cream at 0, full accent at 1. */
export function ramp(t: number): string {
  const k = clamp(t, 0, 1);
  const c = HEAT_FROM.map((x, i) => Math.round(x + (HEAT_TO[i]! - x) * k));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}

/** Legible text colour over `ramp(t)`. Flips to cream once the fill is dark
 *  enough to lose --text-primary against it. */
export const textOn = (t: number) => (t > 0.52 ? '#fff1e5' : '#1a1410');

/** Left-to-right sample of the ramp, for legends. */
export const rampGradient = () => `linear-gradient(90deg, ${ramp(0)}, ${ramp(0.5)}, ${ramp(1)})`;

// Neutral shades for cells that carry NO value — deliberately outside the ramp
// so "no data" can never be misread as "low value". Used for the diagonal hatch
// and as the empty track behind inline bars.
export const NA_HATCH_LIGHT = '#efe7da';
export const NA_HATCH_DARK = '#e3d8c6';

/** Diagonal hatch marking an n/a cell. */
export const NA_HATCH = `repeating-linear-gradient(45deg,${NA_HATCH_LIGHT},${NA_HATCH_LIGHT} 4px,${NA_HATCH_DARK} 4px,${NA_HATCH_DARK} 8px)`;
